import type { LocalTradeFilters, TradeListParams } from '@/types';
import { TERMINAL_TRADE_STATUSES } from '@/types';

/**
 * LR-546: translate the blotter's client-side filter/search/sort state into the
 * backend query params (matches PositionFilter in api/v1/positions/views.py).
 *
 * The server now applies most of the sidebar predicates, so the blotter pulls a
 * small window instead of the whole book. A thin client filter pass over the
 * loaded window still runs in TradesPage for what the server can't express
 * (daily earnings), see the LR-546 plan's "accepted limitations". Sorting is
 * server-only.
 */

// Non-terminal effective statuses = the LIVE blotter scope (open book + the two
// derived buckets + the attention statuses). History mode = terminal only.
const LIVE_STATUSES: readonly string[] = [
  'pending',
  'pending_open_settlement',
  'active',
  'matured',
  'rejected',
  'partially_settled',
  'pending_partial_settlement',
];
const HISTORY_STATUSES: readonly string[] = TERMINAL_TRADE_STATUSES;

// The only sortable columns: each is a key of the backend's PositionFilter.ordering,
// whose order matches what the cell shows (computed figures through its `sort_keys`).
// The blotter holds a window of the book, so it never re-sorts rows itself; a column
// missing here (COB, Rate Type) has no sort at all.
// Nothing checks this set against the backend: an id added here without its key in
// PositionFilter.ordering 400s the whole blotter load. Add it to both, and to
// test_ordering_accepts_every_blotter_column_key in test_position_query_surface.py.
export const SERVER_ORDERABLE_COLUMNS: ReadonlySet<string> = new Set<string>([
  'id', 'symbol_id', 'description', 'isin', 'quantity', 'gross_price',
  'current_market_price', 'trade_date', 'settlement_date', 'actual_settlement_date',
  'maturity_date', 'currency', 'settlement_currency', 'custodian_account',
  'counterparty_account', 'collateral_isin', 'created_at', 'updated_at',
  'trade_type_display', 'agreement_display', 'term_type_display', 'counterparty',
  'status_display', 'custodian_name', 'latest_mark_price', 'margin', 'rate_entry_mode',
  'benchmark_name', 'spread_bps', 'fee_percent', 'delivery_type', 'settlement_venue',
  'cash_settled', 'securities_settled', 'booking_center', 'collateral_type',
  'collateral_quantity', 'pre_delivery_party',
  'margin_price', 'contract_value', 'trade_gross_value', 'fx_rate', 'marked_margin_price',
  'last_marked_gross_value', 'marked_contract_value', 'collateral_value', 'total_rate',
  'fee_income', 'net_amount', 'closing_cash', 'daily_earnings', 'cost_of_collateral',
]);

export const isServerOrderable = (columnId: string): boolean =>
  SERVER_ORDERABLE_COLUMNS.has(columnId);

// LR-697: History → Trades' default when neither the user nor the active saved view
// has picked a sort — "what closed last", not the live book's "what traded last"
// (the class-level `-trade_date` default in PositionViewSet.queryset). Backend-only:
// deliberately absent from SERVER_ORDERABLE_COLUMNS above, since it has no column of
// its own to click — clicking Close Settlement Date still sends `actual_settlement_date`
// (unchanged, see PositionFilter.ordering) and sorts the raw close_settlement_date.
const HISTORY_DEFAULT_ORDERING = '-history_close_date';

const csv = (values: readonly string[]): string | undefined =>
  values.length ? values.join(',') : undefined;

// django-filter drops an EMPTY csv value (→ matches EVERYTHING), so "match nothing"
// (disjoint intersections below) needs a sentinel no real status/name equals.
export const MATCH_NOTHING = '__lr_match_nothing__';

const modeStatuses = (mode: string | undefined): readonly string[] =>
  mode === 'history' ? HISTORY_STATUSES : LIVE_STATUSES;

/** User's status selection scoped to the mode, else the whole mode set. */
const resolveStatuses = (
  selected: readonly string[],
  mode: string | undefined,
): string | undefined => {
  const scope = modeStatuses(mode);
  if (selected.length) {
    const within = selected.filter((s) => scope.includes(s));
    return within.length ? within.join(',') : MATCH_NOTHING;
  }
  return scope.join(',');
};

// Match-nothing id: no CP has a negative id, so counterparty__in=[-1] → zero rows.
export const MATCH_NOTHING_ID = -1;

export interface ServerQueryInput {
  localFilters: LocalTradeFilters;
  searchQuery: string;
  /** TanStack Table sort state (first entry wins), or []. */
  sorting: { id: string; desc: boolean }[];
  /** Counterparties the active view is scoped to ([] = All Trades). */
  viewCounterparties: string[];
  /** Counterparty name -> FK id — sent as `counterparty_ids` (comma-safe, unlike
   *  a CSV of names). */
  cpNameToId: Record<string, number>;
  mode: string | undefined;
}

/** The sidebar filters shared by the list, attention and metrics queries. */
const baseFilterParams = (f: LocalTradeFilters): Partial<TradeListParams> => ({
  symbols: csv(f.symbols),
  trade_types: csv(f.tradeTypes),
  collateral_types: csv(f.collateralTypes),
  agreement_types: csv(f.agreementTypes),
  term_types: csv(f.termTypes),
  currencies: csv(f.currencies),
  settlement_currencies: csv(f.settlementCurrencies),
  trade_date_from: f.tradeDateFrom ?? undefined,
  trade_date_to: f.tradeDateTo ?? undefined,
  settlement_date_from: f.settlementDateFrom ?? undefined,
  settlement_date_to: f.settlementDateTo ?? undefined,
  maturity_date_from: f.maturityDateFrom ?? undefined,
  maturity_date_to: f.maturityDateTo ?? undefined,
  created_at_from: f.createdAtFrom ?? undefined,
  created_at_to: f.createdAtTo ?? undefined,
  updated_at_from: f.updatedAtFrom ?? undefined,
  updated_at_to: f.updatedAtTo ?? undefined,
  quantity_min: f.quantityMin ?? undefined,
  quantity_max: f.quantityMax ?? undefined,
  contract_value_min: f.contractValueMin ?? undefined,
  contract_value_max: f.contractValueMax ?? undefined,
  gross_price_min: f.grossPriceMin ?? undefined,
  gross_price_max: f.grossPriceMax ?? undefined,
  margin_min: f.marginMin ?? undefined,
  margin_max: f.marginMax ?? undefined,
  // NB: dailyEarnings range is intentionally omitted — the backend can't filter
  // the rate-engine property; TradesPage applies it client-side over the window.
});

/**
 * Effective CP scope = view ∩ sidebar. `'ALL'` = no constraint; `[]` = match nothing
 * (disjoint). Shared by the list params AND use-view-metrics so the table and the
 * MetricsBar agree on scope.
 */
export const resolveViewCounterparties = (
  view: readonly string[],
  sidebar: readonly string[],
): string[] | 'ALL' => {
  if (view.length && sidebar.length) return view.filter((c) => sidebar.includes(c));
  if (view.length) return [...view];
  if (sidebar.length) return [...sidebar];
  return 'ALL';
};

/** Effective CP scope as a `counterparty_ids` param: undefined = all, sentinel id =
 *  match nothing. Fails CLOSED if the scope is empty OR any name in it doesn't resolve
 *  (registry not loaded, or a rename left a stale name): sending only the resolved
 *  subset would silently mislabel the scope (a 3-CP view showing 2). */
const counterpartyIdsParam = (
  view: readonly string[],
  sidebar: readonly string[],
  nameToId: Record<string, number>,
): string | undefined => {
  const cps = resolveViewCounterparties(view, sidebar);
  if (cps === 'ALL') return undefined;
  const ids = cps.map((n) => nameToId[n]).filter((id): id is number => id != null);
  if (cps.length === 0 || ids.length !== cps.length) return String(MATCH_NOTHING_ID);
  return ids.join(',');
};

/** Body params for the windowed list (`useTradesInfinite`). */
export const buildTradesServerParams = (
  input: ServerQueryInput,
): Omit<TradeListParams, 'limit' | 'offset'> => {
  const { localFilters: f, searchQuery, sorting, viewCounterparties, cpNameToId, mode } = input;
  // A persisted / saved-view sort on a column the server can't order counts as no sort.
  const sort = sorting[0] && isServerOrderable(sorting[0].id) ? sorting[0] : undefined;
  // No sort at all (neither the user nor a saved view chose one — LR-671 priority is
  // already respected: a saved view's sort arrives here pre-loaded into `sorting`, same
  // as a manual click) still means "let the server default win" in live mode, exactly
  // as before. In History it means the OTHER server default: closed-last, not traded-last.
  const ordering = sort
    ? `${sort.desc ? '-' : ''}${sort.id}`
    : (mode === 'history' ? HISTORY_DEFAULT_ORDERING : undefined);
  return {
    ...baseFilterParams(f),
    search: searchQuery.trim() || undefined,
    ordering,
    statuses: resolveStatuses(f.statuses, mode),
    counterparty_ids: counterpartyIdsParam(viewCounterparties, f.counterparties, cpNameToId),
  };
};

/** Metrics params for the MetricsBar summary: same filters + search, scoped to the
 *  mode, but WITHOUT `trade_types` (the bar's L/B & Repo sections ARE the
 *  trade-type selector — LR-469) and WITHOUT counterparties/ordering (the summary
 *  returns all CPs; use-view-metrics picks the active view's CPs). */
export const buildTradesMetricsParams = (
  input: Pick<ServerQueryInput, 'localFilters' | 'searchQuery' | 'mode'>,
): Omit<TradeListParams, 'limit' | 'offset' | 'ordering'> => {
  const { localFilters: f, searchQuery, mode } = input;
  const params = baseFilterParams(f);
  delete params.trade_types;
  return {
    ...params,
    search: searchQuery.trim() || undefined,
    statuses: resolveStatuses(f.statuses, mode),
  };
};

/** Tab-count params: mode scope only (badges are independent of sidebar/search —
 *  LR-104 review L-2), all CPs. */
export const buildTradesTabCountParams = (
  mode: string | undefined,
): Omit<TradeListParams, 'limit' | 'offset' | 'ordering'> => ({
  statuses: modeStatuses(mode).join(','),
});

/** Sidebar option-pool scope: mode + view CPs only — NO sidebar filters/search, so
 *  the Security/Currency dropdowns stay stable (don't shrink as the user narrows). */
export const buildTradesOptionsParams = (
  mode: string | undefined,
  viewCounterparties: readonly string[],
  cpNameToId: Record<string, number>,
): Omit<TradeListParams, 'limit' | 'offset' | 'ordering'> => ({
  statuses: modeStatuses(mode).join(','),
  counterparty_ids: counterpartyIdsParam(viewCounterparties, [], cpNameToId),
});

/**
 * LR-700: Term pill slice — view∩sidebar CPs + filters + search, minus ONLY its
 * own `term_types` predicate, so every term type present stays listed (and
 * pickable) whatever the current selection.
 *
 * Deliberately NARROWER than `buildTradesPillParams`, which also drops
 * `trade_types`: the ccy pill does that because the bar's Stock L/B & Repo
 * sections ARE the trade-type selector, and a currency must stay pickable across
 * families (LR-474 D3). The Term pill has no such twin — dropping trade_types
 * would print counts that disagree with the book the operator is looking at
 * under an active family preset.
 *
 * `mode` is a real input here, not a constant: unlike the ccy pill (live-only,
 * MetricsBar), this pill also renders in History → Trades, where the counts must
 * scope to the terminal statuses.
 */
export const buildTradesTermPillParams = (
  input: Pick<ServerQueryInput, 'localFilters' | 'searchQuery' | 'viewCounterparties' | 'cpNameToId' | 'mode'>,
): Omit<TradeListParams, 'limit' | 'offset' | 'ordering'> => {
  const { localFilters: f, searchQuery, viewCounterparties, cpNameToId, mode } = input;
  const params = baseFilterParams(f);
  delete params.term_types;
  return {
    ...params,
    search: searchQuery.trim() || undefined,
    statuses: resolveStatuses(f.statuses, mode),
    counterparty_ids: counterpartyIdsParam(viewCounterparties, f.counterparties, cpNameToId),
  };
};

/** MetricsBar pill slice: the same basis the pill lists over — view∩sidebar CPs +
 *  filters + search, minus its own settlement-ccy predicate and trade_types. */
export const buildTradesPillParams = (
  input: Pick<ServerQueryInput, 'localFilters' | 'searchQuery' | 'viewCounterparties' | 'cpNameToId' | 'mode'>,
): Omit<TradeListParams, 'limit' | 'offset' | 'ordering'> => {
  const { localFilters: f, searchQuery, viewCounterparties, cpNameToId, mode } = input;
  const params = baseFilterParams(f);
  delete params.trade_types;
  delete params.settlement_currencies;
  return {
    ...params,
    search: searchQuery.trim() || undefined,
    statuses: resolveStatuses(f.statuses, mode),
    counterparty_ids: counterpartyIdsParam(viewCounterparties, f.counterparties, cpNameToId),
  };
};
